import { FC, Fragment, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import DOMPurify from 'dompurify';
import { IProductDetail } from '@modules/products/domain/models/productDetail.model';
import { ShareButton } from '@components/button';
import HeartButton from '@components/heart/HeartButton';
import { useScreen } from '@shared/helpers/screen.helper';
import { Global } from '@shared/styles/GlobalStyle';
import { ICollapseItems } from './collapse/Collapse';
import { ButtonContainer, BuyButton, ProductDetailContainer } from './Detail.style';
import GeneralInformation from './generalInformation/GeneralInformation';
import MainDetail from './main/Main';
import ProductDetailNavigation from './navigation/Navigation';
const ProductCollapse = dynamic(() => import('./collapse/Collapse'));
const NutritionContent = dynamic(() => import('./nutrition/Content'));
const DistributionChannel = dynamic(() => import('../distributionChannel/DistributionChannel'));
interface Props {
  data: IProductDetail;
  headerVisibility: boolean;
  setHeaderVisibility: (val: boolean) => void;
  isLike: boolean;
  onUpdateLikeState: () => void;
}
const ProductDetail: FC<Props> = ({
  data,
  headerVisibility,
  setHeaderVisibility,
  isLike,
  onUpdateLikeState
}: Props) => {
  const {
    isDesktop
  } = useScreen();
  const [elOffset, setElOffset] = useState({
    ingredients: 0,
    generalInformation: 0,
    nutritionalInformation: 0,
    howToCook: 0,
    storageMethod: 0,
    distributionChannels: 0
  });
  const [openSection, setOpenSection] = useState<ICollapseItems['key'][]>([]);
  const [triggerElHeight, setTriggerElHeight] = useState(false);
  const [menuList, setMenuList] = useState([]);
  const navigatorForMenuList = {
    generalInformation: {
      label: 'ข้อมูลผลิตภัณฑ์',
      value: 'general-information',
      offset: elOffset.generalInformation
    },
    nutritionInformation: {
      label: 'ข้อมูลทางโภชนาการ',
      value: 'nutritional-information',
      offset: elOffset.nutritionalInformation,
      isNull: data.nutrition === null || data.nutrition === undefined || data.nutrition === ''
    },
    distributionChannels: {
      label: 'ช่องทางจัดจำหน่าย',
      value: 'distribution-channels',
      offset: elOffset.distributionChannels,
      isNull: data.distributionChannel === null
    }
  };
  useEffect(() => {
    setOpenSection(sectionList.map(({
      key
    }) => key));
  }, []);
  const onSelectShop = async () => {
    const openSectionRemoveDuplicateVal = [...new Set([...openSection, 'distribution-channels' as ICollapseItems['key']])];
    setOpenSection(openSectionRemoveDuplicateVal);
    setTimeout(updateTrigger, 800);
    window.scroll({
      top: elOffset.distributionChannels,
      left: 0,
      behavior: 'smooth'
    });
  };
  const updateTrigger = () => {
    setTriggerElHeight(!triggerElHeight);
  };
  const sectionList: ICollapseItems[] = [{
    key: 'ingredients',
    title: 'ส่วนประกอบ',
    children: <Global.WysiwygContent dangerouslySetInnerHTML={{
      __html: DOMPurify.sanitize(data.ingredients)
    }} />
  }, {
    key: 'general-information',
    title: 'ข้อมูลผลิตภัณฑ์',
    children: <GeneralInformation data={data.generalInfo} />
  }, {
    key: 'nutritional-information',
    title: 'ข้อมูลทางโภชนาการ',
    children: data.nutrition && <NutritionContent data={data.nutrition} updateTrigger={updateTrigger} />
  }, {
    key: 'how-to-cook',
    title: 'วิธีการปรุง',
    children: data.cooking && <Global.WysiwygContent dangerouslySetInnerHTML={{
      __html: DOMPurify.sanitize(data.cooking)
    }} />
  }, {
    key: 'storage-method',
    title: 'วิธีการเก็บรักษา',
    children: data.preserve && <Global.WysiwygContent dangerouslySetInnerHTML={{
      __html: DOMPurify.sanitize(data.preserve)
    }} />
  }, {
    key: 'distribution-channels',
    title: 'ช่องทางจัดจำหน่าย',
    children: data.distributionChannel && <DistributionChannel data={data.distributionChannel} />
  }];
  useEffect(() => {
    checkHaveMenuListValue();
  }, [elOffset]);
  const checkHaveMenuListValue = () => {
    const list = [navigatorForMenuList.generalInformation];
    if (!navigatorForMenuList.nutritionInformation.isNull) list.push(navigatorForMenuList.nutritionInformation);
    if (!navigatorForMenuList.distributionChannels.isNull) list.push(navigatorForMenuList.distributionChannels);
    setMenuList(list);
  };
  return <ProductDetailContainer>
            {isDesktop && <ProductDetailNavigation elOffset={elOffset} openSection={openSection} setOpenSection={setOpenSection} updateTrigger={updateTrigger} headerVisibility={headerVisibility} setHeaderVisibility={setHeaderVisibility} menuList={menuList} />}
            <MainDetail title={data.name} description={data.description} tag={data.tag} like={data.like} />
            <ButtonContainer>
                {data.distributionChannel && <BuyButton onClick={() => onSelectShop()}>
                        <Image src="/icons/shop-icon.svg" alt="shop-icon" width={24} height={24} />
                        ซื้อเลย
                    </BuyButton>}
                <HeartButton isLike={isLike} onUpdateLikeState={onUpdateLikeState} />
                <ShareButton caption={data.name} path={window.location.pathname} />
            </ButtonContainer>

            {sectionList?.map(({
      title,
      key,
      children
    }) => <Fragment key={key}>
                    {children && <ProductCollapse title={title} key={key} sectionKey={key} setElOffset={setElOffset} openSection={openSection} setOpenSection={setOpenSection} triggerElHeight={triggerElHeight} updateTrigger={updateTrigger}>
                            {children}
                        </ProductCollapse>}
                </Fragment>)}
        </ProductDetailContainer>;
};
export default ProductDetail;